/**
 * THE SEA'S DRY TRIANGLES (sweep 2026-10-05, perf). The Commons sea is one 384² grid (295k triangles) riding the camera
 * over a 3 km square, and on a coast most of it lies over land: every one of those triangles still ran the Gerstner
 * vertex stage before the shore discard threw its fragments away. After each shore bake the index keeps only the quads
 * within `dilate` cells of a vertex whose bed is under the sea plus `wetMarginM` — the swash, the swell's horizontal
 * travel and the shoaled crests all stay inside that band — in the SAME order and winding as three's PlaneGeometry, so
 * a fully wet bake is byte-identical to the full grid. Vertices are untouched: every drawn-surface query (the rider law,
 * the waterline lens, the FFT mean) solves the grid analytically and never reads the index.
 */

/** Bed height (m) at the shore lattice point nearest mesh vertex (ix, iy); `bed` is shoreN × shoreN, row = z. */
function bedAtVertex(bed: Float32Array, shoreN: number, seg: number, ix: number, iy: number): number {
  const col = Math.round((ix / seg) * (shoreN - 1));
  const row = Math.round((iy / seg) * (shoreN - 1));
  return bed[row * shoreN + col];
}

/**
 * Write the kept triangles of a `seg` × `seg` PlaneGeometry (rotated −π/2 about X: vertex row iy runs toward +Z, the
 * shore lattice's row order) into `out` (length ≥ seg²·6) and return the index count.
 */
export function buildSeaIndex(
  out: Uint32Array, seg: number, bed: Float32Array, shoreN: number, seaLevel: number,
  wetMarginM = 3, dilate = 1,
): number {
  const n1 = seg + 1;
  const wetLevel = seaLevel + wetMarginM;
  // A quad is "wet" when any corner's bed is under the band.
  const vWet = new Uint8Array(n1 * n1);
  for (let iy = 0; iy < n1; iy++) for (let ix = 0; ix < n1; ix++) vWet[iy * n1 + ix] = bedAtVertex(bed, shoreN, seg, ix, iy) < wetLevel ? 1 : 0;
  let q = new Uint8Array(seg * seg);
  for (let iy = 0; iy < seg; iy++) {
    for (let ix = 0; ix < seg; ix++) {
      const a = iy * n1 + ix;
      q[iy * seg + ix] = vWet[a] | vWet[a + 1] | vWet[a + n1] | vWet[a + n1 + 1];
    }
  }
  // Dilate by `dilate` quads (a separable max): the crest of a wave over the last wet quad leans into the next one.
  for (let pass = 0; pass < dilate; pass++) {
    const h = new Uint8Array(seg * seg);
    for (let iy = 0; iy < seg; iy++) for (let ix = 0; ix < seg; ix++) {
      const i = iy * seg + ix;
      h[i] = q[i] | (ix > 0 ? q[i - 1] : 0) | (ix < seg - 1 ? q[i + 1] : 0);
    }
    const v = new Uint8Array(seg * seg);
    for (let iy = 0; iy < seg; iy++) for (let ix = 0; ix < seg; ix++) {
      const i = iy * seg + ix;
      v[i] = h[i] | (iy > 0 ? h[i - seg] : 0) | (iy < seg - 1 ? h[i + seg] : 0);
    }
    q = v;
  }
  // three's PlaneGeometry order: per quad (a, b, d), (b, c, d) with a = ix + n1·iy, b = ix + n1·(iy+1),
  // c = ix+1 + n1·(iy+1), d = ix+1 + n1·iy.
  let k = 0;
  for (let iy = 0; iy < seg; iy++) {
    for (let ix = 0; ix < seg; ix++) {
      if (!q[iy * seg + ix]) continue;
      const a = ix + n1 * iy, b = ix + n1 * (iy + 1), c = ix + 1 + n1 * (iy + 1), d = ix + 1 + n1 * iy;
      out[k++] = a; out[k++] = b; out[k++] = d;
      out[k++] = b; out[k++] = c; out[k++] = d;
    }
  }
  return k;
}
